<script setup>
import { ref, computed, watch } from 'vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import AutomationInteractiveCardItem from './AutomationInteractiveCardItem.vue';
import AutomationQuickReplyItem from './AutomationQuickReplyItem.vue';
import AutomationInteractivePreview from './AutomationInteractivePreview.vue';

const props = defineProps({
  modelValue: { type: [Object, Array], default: () => [] },
});
const emit = defineEmits(['update:modelValue']);
const MAX_CARDS = 12;
const MAX_QUICK_REPLIES = 13;

const emptyAction = () => ({
  type: 'postback',
  text: '',
  payload: '',
  uri: '',
});
const emptyCard = () => ({
  title: '',
  description: '',
  media_url: '',
  actions: [emptyAction()],
});
const emptyQuickReply = () => ({
  title: '',
  value: '',
});

const cloneCard = item => ({
  title: item.title || '',
  description: item.description || '',
  media_url: item.media_url || '',
  actions: item.actions?.length
    ? item.actions.map(action => ({ ...emptyAction(), ...action }))
    : [emptyAction()],
});

const cloneQuickReply = item => ({
  title: item.title || '',
  value: item.value || '',
});

const normalize = value => {
  const source = Array.isArray(value) ? value[0] : value;
  return {
    mode: source?.mode === 'quick_replies' ? 'quick_replies' : 'cards',
    items: source?.items?.length ? source.items.map(cloneCard) : [emptyCard()],
    quickReplies: source?.quick_replies?.length
      ? source.quick_replies.map(cloneQuickReply)
      : [],
  };
};

const state = ref(normalize(props.modelValue));

watch(
  () => props.modelValue,
  value => {
    state.value = normalize(value);
  }
);

const emitUpdate = () => {
  const payload = { mode: state.value.mode };
  if (state.value.mode === 'cards') {
    payload.items = state.value.items;
  } else {
    payload.quick_replies = state.value.quickReplies;
  }
  emit('update:modelValue', payload);
};

const setMode = mode => {
  state.value.mode = mode;
  emitUpdate();
};

const canAddCard = computed(() => state.value.items.length < MAX_CARDS);
const canAddQuickReply = computed(
  () => state.value.quickReplies.length < MAX_QUICK_REPLIES
);

const addCard = () => {
  if (!canAddCard.value) return;
  state.value.items.push(emptyCard());
  emitUpdate();
};

const removeCard = index => {
  state.value.items.splice(index, 1);
  emitUpdate();
};

const updateCard = (index, value) => {
  state.value.items.splice(index, 1, value);
  emitUpdate();
};

const addQuickReply = () => {
  if (!canAddQuickReply.value) return;
  state.value.quickReplies.push(emptyQuickReply());
  emitUpdate();
};

const removeQuickReply = index => {
  state.value.quickReplies.splice(index, 1);
  emitUpdate();
};

const updateQuickReply = (index, value) => {
  state.value.quickReplies.splice(index, 1, value);
  emitUpdate();
};
</script>

<template>
  <div class="mt-2 p-4 rounded-xl border border-n-weak bg-n-solid-1">
    <div
      class="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_18rem] gap-6 items-start"
    >
      <div class="flex flex-col gap-4 min-w-0">
        <div
          class="inline-flex self-start p-1 gap-1 rounded-lg bg-n-slate-3 dark:bg-n-solid-3"
        >
          <NextButton
            :variant="state.mode === 'cards' ? 'solid' : 'ghost'"
            :color="state.mode === 'cards' ? 'blue' : 'slate'"
            icon="i-lucide-layout-grid"
            size="sm"
            :label="$t('AUTOMATION.INTERACTIVE_MESSAGE.MODE_CARDS')"
            @click="setMode('cards')"
          />
          <NextButton
            :variant="state.mode === 'quick_replies' ? 'solid' : 'ghost'"
            :color="state.mode === 'quick_replies' ? 'blue' : 'slate'"
            icon="i-lucide-message-square"
            size="sm"
            :label="$t('AUTOMATION.INTERACTIVE_MESSAGE.MODE_QUICK_REPLIES')"
            @click="setMode('quick_replies')"
          />
        </div>

        <div v-if="state.mode === 'cards'" class="flex flex-col gap-2.5">
          <div class="flex items-center justify-between">
            <label class="text-sm font-medium text-n-slate-12">
              {{ $t('AUTOMATION.INTERACTIVE_MESSAGE.CARDS_LABEL') }}
            </label>
            <span
              class="text-xs text-n-slate-11 bg-n-alpha-1 rounded-full px-2 py-0.5"
            >
              {{ state.items.length }}/{{ MAX_CARDS }}
            </span>
          </div>
          <AutomationInteractiveCardItem
            v-for="(card, index) in state.items"
            :key="index"
            :model-value="card"
            @update:model-value="value => updateCard(index, value)"
            @remove="removeCard(index)"
          />
          <NextButton
            icon="i-lucide-plus"
            blue
            faded
            sm
            justify="center"
            :disabled="!canAddCard"
            :label="$t('AUTOMATION.INTERACTIVE_MESSAGE.ADD_CARD')"
            class="w-full"
            @click="addCard"
          />
          <p class="flex items-center gap-1 text-xs text-n-slate-9">
            <span class="i-lucide-info shrink-0 size-3.5" aria-hidden="true" />
            {{ $t('AUTOMATION.INTERACTIVE_MESSAGE.CARDS_CAP_HELP') }}
          </p>
        </div>

        <div v-else class="flex flex-col gap-2.5">
          <div class="flex items-center justify-between">
            <label class="text-sm font-medium text-n-slate-12">
              {{ $t('AUTOMATION.INTERACTIVE_MESSAGE.QUICK_REPLIES_LABEL') }}
            </label>
            <span
              class="text-xs text-n-slate-11 bg-n-alpha-1 rounded-full px-2 py-0.5"
            >
              {{ state.quickReplies.length }}/{{ MAX_QUICK_REPLIES }}
            </span>
          </div>
          <AutomationQuickReplyItem
            v-for="(quickReply, index) in state.quickReplies"
            :key="index"
            :model-value="quickReply"
            @update:model-value="value => updateQuickReply(index, value)"
            @remove="removeQuickReply(index)"
          />
          <NextButton
            icon="i-lucide-plus"
            blue
            faded
            sm
            justify="center"
            :disabled="!canAddQuickReply"
            :label="$t('AUTOMATION.INTERACTIVE_MESSAGE.ADD_QUICK_REPLY')"
            class="w-full"
            @click="addQuickReply"
          />
          <p class="flex items-center gap-1 text-xs text-n-slate-9">
            <span class="i-lucide-info shrink-0 size-3.5" aria-hidden="true" />
            {{ $t('AUTOMATION.INTERACTIVE_MESSAGE.QUICK_REPLIES_CAP_HELP') }}
          </p>
          <p class="text-xs text-n-slate-9">
            {{
              $t('AUTOMATION.INTERACTIVE_MESSAGE.QUICK_REPLIES_CARRIER_HELP')
            }}
          </p>
        </div>
      </div>

      <div
        class="lg:sticky lg:top-4 pt-4 lg:pt-0 border-t border-n-weak lg:border-t-0 lg:border-l lg:pl-6"
      >
        <AutomationInteractivePreview
          :mode="state.mode"
          :items="state.items"
          :quick-replies="state.quickReplies"
        />
      </div>
    </div>
  </div>
</template>
